import { collectBuffers } from './collectBuffers.js'
import { RECYCLE_HEAP_BYTES, trapped } from './gridRun.js'

const RESPONSE = '__RESPONSE__'

export const NEEDS_MODEL = new Set(['jscadMain', 'jscadExportData', 'jscadMeasure', 'jscadCheck'])
export const NEEDS_SOLIDS = new Set(['jscadExportData', 'jscadMeasure', 'jscadCheck'])

const ignore = () => {}

/**
 * The frame's workers: the active one the app's requests go to, the members
 * of grid runs, and one idle worker kept warm. Every worker gets the app's
 * setup; one without the current script loads it before it runs.
 *
 * @typedef {import('./workerSlot.js').Slot} Slot
 * @typedef {import('./workerSlot.js').Entry} Entry
 * @typedef {{slots: Slot[], active: Slot | null, mirrored: object[], lastScript: object | undefined,
 *   sentScript: object | undefined, lastSource: string | undefined, lastMain: object | undefined,
 *   poolSize: number, timeoutMs: number}} State
 * @param {object} options
 * @param {State} options.state
 * @param {ReturnType<typeof import('./workerSlot.js').createSlots>} options.slotOps
 * @param {(message: unknown, transfer?: Transferable[]) => void} options.post
 * @param {(id: unknown, name: string, message: string) => void} options.answerError
 * @param {(slot: Slot) => boolean} options.busy - a member of a grid run
 * @param {(slot: Slot) => boolean} options.inGrid - a member of a run that fanned out
 * @param {(slot: Slot, message: any, entry: Entry) => import('./gridRun.js').Run | null} options.openRun
 * @param {(slot: Slot, reason: string) => void} [options.onExit] - the frame answers requests it held back
 */
export const createPool = ({ state, slotOps, post, answerError, busy, inGrid, openRun, onExit = () => {} }) => {
  const idle = (slot) => slot !== state.active && !busy(slot)

  // Sent without a transfer list: a mirrored message is kept for the next worker.
  const request = (slot, message, onAnswer, setup) => {
    slot.worker.postMessage({ ...message, id: slotOps.track(slot, { method: message.method, onAnswer, setup }) })
  }

  const start = ({ replay }) => {
    const slot = slotOps.start()
    state.slots.push(slot)
    if (replay) for (const message of state.mirrored) request(slot, message, ignore, message)
    return slot
  }

  const tryStart = () => {
    try {
      return start({ replay: true })
    } catch {
      return null
    }
  }

  const remove = (slot) => {
    state.slots = state.slots.filter((s) => s !== slot)
  }

  // An idle worker that already holds the current script needs no reload.
  const pickIdle = () => {
    const free = state.slots.filter(idle)
    return free.find((slot) => slot.script === state.lastScript) ?? free[0] ?? null
  }

  // With a run's members that makes poolSize + 1 workers at most.
  const ensureSpare = () => {
    if (state.slots.some(idle) || state.slots.length > state.poolSize) return
    tryStart()
  }

  // Each worker holds its own bundles, file map and WASM heap, so once a run
  // ends only one idle worker stays warm: the smallest heap under the budget.
  const trim = () => {
    const small = state.slots.filter((slot) => idle(slot) && slot.heap < RECYCLE_HEAP_BYTES)
      .sort((a, b) => a.heap - b.heap)
    const keep = small.find((slot) => slot.script === state.lastScript) ?? small[0]
    for (const slot of state.slots.filter(idle)) {
      if (slot === keep) continue
      remove(slot)
      slotOps.end(slot, null, 'the grid run ended', null)
    }
  }

  const mirror = (message) => {
    for (const slot of state.slots) if (slot !== state.active) request(slot, message, ignore, message)
  }

  // Setup the app sent the retired worker went to the promoted one too, so the
  // promoted worker's answer stands in for it.
  const handOver = (from, to) => {
    for (const [workerId, pending] of from.pending) {
      if (pending.onAnswer || !pending.setup) continue
      const copy = [...to.pending.values()].find((r) => r.setup === pending.setup)
      const answer = to.setupAnswers.get(pending.setup)
      if (!copy && !answer) continue
      clearTimeout(pending.timer)
      from.pending.delete(workerId)
      if (copy) Object.assign(copy, { appId: pending.appId, onAnswer: undefined })
      else post({ ...answer, id: pending.appId })
    }
  }

  // For a trapped WebAssembly instance or a superseded run. The app is not
  // told: the promoted worker already holds its setup and reloads the script on demand.
  const retire = (slot, reason) => {
    if (slot === state.active) {
      const replacement = pickIdle() ?? tryStart()
      // Without a replacement the old worker keeps serving: a trapped one
      // answers errors, but the app stays live for a transient failure.
      if (!replacement) return
      remove(slot)
      state.active = replacement
      handOver(slot, state.active)
    } else remove(slot)
    onExit(slot, reason)
    slotOps.end(slot, null, reason, null)
    if (state.active) ensureSpare()
  }

  // All workers ran model code from the previous source: end them so the
  // next load starts clean, answering what they held. Mirrored setup
  // (inits, file map) stays for the fresh worker's replay.
  const reset = (reason) => {
    for (const slot of [...state.slots]) {
      remove(slot)
      onExit(slot, reason)
      slotOps.end(slot, null, reason, null)
    }
    state.active = null
  }

  const kill = (slot, expiredId, reason, errorName) => {
    const inGridRun = inGrid(slot)
    remove(slot)
    onExit(slot, reason)
    slotOps.end(slot, expiredId, reason, errorName)
    if (slot !== state.active) return
    state.active = pickIdle()
    // A grid run goes on without this worker, so the app has nothing to replay
    if (inGridRun) state.active ??= state.slots[0] ?? tryStart()
    else post({ method: 'frameWorkerTerminated', params: [{ reason }] })
    if (state.active) ensureSpare()
  }

  // The user stopped the active run. Same removal as kill, minus the
  // frameWorkerTerminated post: that notice makes the app replay the script
  // and the last run, restarting the model the user just stopped. Answers
  // what the worker held, promotes a spare or starts a fresh worker.
  const abortActive = (reason) => {
    if (!state.active) return false
    const slot = state.active
    remove(slot)
    onExit(slot, reason)
    slotOps.end(slot, null, reason, 'AbortError')
    state.active = pickIdle() ?? tryStart()
    if (state.active) ensureSpare()
    return true
  }

  const dispatch = (slot, message, entry) => {
    if (!entry) {
      slot.worker.postMessage(message, collectBuffers(message))
      return
    }
    const run = openRun(slot, message, entry)
    const out = { ...message, id: slotOps.track(slot, run ? { ...entry, run } : entry) }
    slot.worker.postMessage(out, collectBuffers(out))
  }

  // A worker joining a run loads the script the run was sent against.
  const scriptFor = (entry) => entry?.run?.script ?? state.lastScript

  // The app does not know about a retire, so a script it sent meanwhile is the
  // model it expects; reloading lastScript would replace it.
  const needsReload = (slot, script) => !!script && slot.script !== script &&
    ![...slot.pending.values()].some((r) => r.method === 'jscadScript' && (!r.onAnswer || r.run))

  // Requests that arrive during the reload wait behind it, so they reach the
  // worker in the order they were sent.
  const relay = (slot, message, entry) => {
    if (slot.queued) slot.queued.push({ message, entry })
    else if (NEEDS_MODEL.has(message.method) && needsReload(slot, scriptFor(entry))) ensureLoaded(slot, message, entry)
    else dispatch(slot, message, entry)
  }

  const release = (slot, error) => {
    const [first, ...rest] = slot.queued
    slot.queued = null
    if (!first) return
    if (!error) dispatch(slot, first.message, first.entry)
    else if (first.entry?.onAnswer) first.entry.onAnswer({ method: RESPONSE, error })
    else if (first.entry) answerError(first.entry.appId, error.name, error.message)
    for (const { message, entry } of rest) relay(slot, message, entry)
  }

  // A worker without the model has the setup but not the script. Export,
  // measure and check read the solids of the last run, so those replay it as
  // well; with no run since the load, the load's own main is that run.
  // A replay answers for the frame, so the worker must not hash meshes for
  // an answer the frame drops: strip the app's held and runId.
  const cleanReplay = (options) => {
    const { held: _held, runId: _runId, ...kept } = options ?? {}
    return kept
  }

  const ensureLoaded = (slot, message, entry) => {
    slot.queued = [{ message, entry }]
    const script = scriptFor(entry)
    const needsSolids = NEEDS_SOLIDS.has(message.method)
    const steps = [{ method: 'jscadScript', params: [{ ...cleanReplay(script), runMain: needsSolids && !state.lastMain }] }]
    if (needsSolids && state.lastMain) {
      steps.push({ method: 'jscadMain', params: [{ ...cleanReplay(state.lastMain), stream: false }] })
    }
    const next = () => {
      const step = steps.shift()
      if (!step) return release(slot)
      request(slot, step, (data) => {
        if (data.error) {
          // A script step that traps poisoned the worker before any model
          // ran: answer the queued requests, then retire it instead of
          // running them on the trapped worker.
          if (step.method === 'jscadScript' && trapped(data)) {
            release(slot, data.error)
            retire(slot, 'the model trapped in WebAssembly')
            return
          }
          return release(slot, data.error)
        }
        if (step.method === 'jscadScript') slot.script = script
        next()
      })
    }
    next()
  }

  return { start, tryStart, pickIdle, ensureSpare, trim, mirror, retire, reset, kill, abortActive, relay }
}
